/**
 * tutorInvoiceAdapter — une facture du tuteur (`tutors.tutoringInvoices`, `GET v2/tutoring-invoices`)
 * → une ligne de « Mes factures », et ses deux liens.
 *
 * Port de la table de `tutor/invoices/TutoringInvoicesScreen.js` : la date (« 31 octobre 2026 »),
 * l'émetteur (« Masteur » quand le serveur ne le dit pas), le destinataire, le montant (`total`, en
 * euros, deux décimales), « Facture » (le PDF `attachment`, sinon sa génération) et « Récapitulatif »
 * (`payment_recap`, seulement s'il existe).
 */
import { formatDayMonthYear } from "../../../ui/formatters/dateFormatter"
import { eurosCents } from "../../../ui/formatters/moneyFormatter"
import { type UITutorInvoice } from "../../../ui/types/tutorCompte"

export type ApiTutoringInvoice = {
    id?: number | null
    date?: string | null
    issuer?: { display_name?: string | null } | null
    recipient?: { display_name?: string | null } | null
    /** En euros ; un nombre (colonne `float`), lu aussi s'il arrivait en chaîne. */
    total?: number | string | null
    attachment?: string | null
    payment_recap?: string | null
}

/** Les adresses d'une ligne, que la page ne montre pas : l'écran les ouvre au toucher. */
export type TutorInvoiceLinks = { attachment: string | null; paymentRecap: string | null }

function toUITutorInvoice(row: ApiTutoringInvoice): UITutorInvoice | null {
    if (row?.id == null) return null
    return {
        id: row.id,
        dateLabel: row.date ? formatDayMonthYear(row.date, false) : "",
        issuer: row.issuer?.display_name ?? "Masteur",
        recipient: row.recipient?.display_name ?? "",
        // Le serveur sert des euros : l'arrondi au centime évite qu'un flottant ne déborde.
        amountLabel: eurosCents(Math.round(Number(row.total ?? 0) * 100)),
        hasAttachment: !!row.attachment,
        hasPaymentRecap: !!row.payment_recap,
    }
}

export function toUITutorInvoices(rows: ApiTutoringInvoice[] | null | undefined): UITutorInvoice[] {
    return (rows ?? []).map(toUITutorInvoice).filter((invoice): invoice is UITutorInvoice => invoice != null)
}

export function toTutorInvoiceLinks(rows: ApiTutoringInvoice[] | null | undefined): Record<number, TutorInvoiceLinks> {
    return Object.fromEntries(
        (rows ?? [])
            .filter((row) => row?.id != null)
            .map((row) => [row.id, { attachment: row.attachment || null, paymentRecap: row.payment_recap || null }])
    )
}
